import tokens from '@kluein/design-tokens';
import { Badge, Select, type SelectOptions } from '@kluein/klue-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { KlueAILogo } from 'components/common/KlueAILogo';
import store from 'store';
import TEST_IDS from 'test-ids';

import type { RootState } from 'store/store.types';

const KLUE_AI_TEMPLATE_ID = '-1';

type TemplatePickerProps = {
  onPick: (templateId: number) => void;
};
export function TemplatePicker({ onPick }: TemplatePickerProps) {
  // Uses rivals API to display templates
  // Templates are just Rivals with the field `isTemplate` set to true
  // See https://github.com/kluein/klue/blob/dev/app/assets/javascripts/modules/new_board_wizard_utils.js#L58
  const { t } = useTranslation();
  const [value, setValue] = useState<string>();
  const options = useSelector((state: RootState) => {
    const options: SelectOptions[] = [
      {
        value: KLUE_AI_TEMPLATE_ID,
        label: t(
          'Common:boardWizard.templatePicker.options.bestPracticesTemplate',
        ),
        rightSlot: (
          <KlueAILogo
            klueIconSize={tokens.iconSize.medium}
            aiIconSize={tokens.iconSize.small}
          />
        ),
      },
      { value: '', label: t('Common:boardWizard.templatePicker.options.none') },
    ];
    store.select.rivals
      .all(state)
      .sort((a, b) => {
        // First sort by isTemplate (true comes first)
        if (a.isTemplate !== b.isTemplate) {
          return a.isTemplate ? -1 : 1;
        }
        // Then sort by name alphabetically
        return a.name.localeCompare(b.name);
      })
      .forEach((rival) => {
        options.push({
          rightSlot: rival.isTemplate ? (
            <Badge
              label={t('Common:boardWizard.templatePicker.types.template')}
              color="blue"
            />
          ) : (
            <Badge label={t('Common:boardWizard.templatePicker.types.board')} />
          ),
          value: rival.id.toString(),
          label: rival.name,
        });
      });
    return options;
  });
  return (
    // TODO Find a component that can group templates and boards
    <Select
      value={value}
      options={options}
      onSelected={(item) => {
        setValue((item as SelectOptions).value);
        // TODO Clean this up when we have a parameter to opt-in to the AI "template"
        const templateId = Math.max(
          0,
          parseInt((item as SelectOptions).value, 10),
        );
        onPick(templateId);
      }}
      data-test-id={TEST_IDS.boardWizard.templatePickerSelect}
    />
  );
}
